import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { parseApiUtc } from '@/utils/apiDate';
import { useTranslation } from "react-i18next";
import { appDateTime } from "@/utils/appDateFormat";
import { useSearchParams } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import {
  CreditCard,
  Search,
  MoreHorizontal,
  CheckCircle2,
  Ban,
  History as HistoryIcon,
  AlertCircle,
  CircleAlert,
  CircleDot,
  X,
  Inbox,
  Mail,
  User as UserIcon,
} from "lucide-react";

import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../../components/ui/Table";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../../components/ui/DropdownMenu";
import Button from "../../../components/ui/Button";
import { Spinner } from "../../../components/ui/Spinner";
import Pagination from "../../../components/Pagination";

import {
  getSubscriptionCompanies,
  getSubscriptionStats,
  getPendingUpgradeRequests,
  dismissUpgradeRequest,
  coerceTier,
  coerceStatus,
  type CompanySubscriptionView,
  type SubscriptionTier,
  type SubscriptionStatus,
  type CompanySubscriptionListResponse,
  type SubscriptionListQuery,
  type SubscriptionStatsView,
  type SubscriptionUpgradeRequestView,
  type BulkOperationResultView,
} from "../../../api/General/Subscription";
import {
  humanizeRelative,
  relativeTime,
  tierVisual,
  initials,
} from "./helpers";

import MarkPaidDialog from "./MarkPaidDialog";
import RevokeDialog from "./RevokeDialog";
import SubscriptionHistorySheet from "./SubscriptionHistorySheet";
import SignupReferencesPanel from "./SignupReferencesPanel";
import PlatformBillingPanel from "./PlatformBillingPanel";
import BulkActionsBar from "./BulkActionsBar";
import BulkMarkPaidDialog from "./BulkMarkPaidDialog";
import BulkRevokeDialog from "./BulkRevokeDialog";
import BulkFailuresDialog from "./BulkFailuresDialog";

/* ───────── debounce hook (no extra dep) ───────── */
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const TIER_OPTIONS: { value: "" | SubscriptionTier; key: string }[] = [
  { value: "", key: "All Tiers" },
  { value: "Free", key: "Free" },
  { value: "Trial", key: "Trial" },
  { value: "Paid", key: "Paid" },
];

const STATUS_OPTIONS: { value: "" | SubscriptionStatus; key: string }[] = [
  { value: "", key: "All Statuses" },
  { value: "Active", key: "Active" },
  { value: "TrialExpired", key: "Trial Expired" },
  { value: "PaymentOverdue", key: "Payment Overdue" },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function SubscriptionsIndex() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";

  /* ──── Top-level tab (Companies vs Platform Billing vs References) ──── */
  // "platform-billing" is the CO-08 surface: once a company pays by card, the billing engine owns its
  // subscription and the Companies tab's mark-paid / extend / revoke actions are refused for it. The
  // replacement levers (credit, cancel, Bespoke, time-boxed access grant) all live on that tab.
  type Tab = "companies" | "platform-billing" | "references";
  const [tab, setTab] = useState<Tab>("companies");

  /* ──── Filter / paging state ──── */
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [tier, setTier] = useState<"" | SubscriptionTier>("");
  const [status, setStatus] = useState<"" | SubscriptionStatus>("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  /* ──── Data state ──── */
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [data, setData] = useState<CompanySubscriptionListResponse | null>(null);
  const [statsData, setStatsData] = useState<SubscriptionStatsView | null>(null);

  /* ──── Dialog state ──── */
  const [activeCompany, setActiveCompany] = useState<CompanySubscriptionView | null>(null);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  /* ──── Bulk selection + bulk dialog state ──── */
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkMarkPaidOpen, setBulkMarkPaidOpen] = useState(false);
  const [bulkRevokeOpen, setBulkRevokeOpen] = useState(false);
  // CO-38 — the partial-failure REPORT. Held in state (not a toast) because the operator has to read
  // it, and a toast that disappears in 4 seconds is the same defect as console.warn in slower form.
  const [bulkFailures, setBulkFailures] = useState<BulkOperationResultView | null>(null);

  /* ──── Upgrade-requests drawer (Phase 7) ──── */
  const [requestsOpen, setRequestsOpen] = useState(false);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requests, setRequests] = useState<SubscriptionUpgradeRequestView[]>([]);
  const [requestsCount, setRequestsCount] = useState(0);

  const loadRequests = useCallback(async () => {
    setRequestsLoading(true);
    try {
      const res = await getPendingUpgradeRequests();
      if (!res.errors || res.errors.length === 0) {
        const items = res.payload ?? [];
        setRequests(items);
        setRequestsCount(items.length);
      }
    } catch {
      // best-effort — leave count stale rather than crash the page
    } finally {
      setRequestsLoading(false);
    }
  }, []);

  // Lightweight count poll: 60s is plenty since SignalR isn't wired for this type yet.
  useEffect(() => {
    loadRequests();
    const id = window.setInterval(loadRequests, 60_000);
    return () => window.clearInterval(id);
  }, [loadRequests]);

  const [searchParams, setSearchParams] = useSearchParams();

  /* ──── Reset page when filters change ──── */
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, tier, status, pageSize]);

  /* ──── Clear bulk selection on filter/page/pageSize change ──── */
  // Selection is scoped to the visible page; once the underlying set of rows
  // changes for any reason, the user's earlier selection no longer reflects
  // what they're looking at — drop it rather than carry stale ids forward.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [debouncedSearch, tier, status, pageSize, page]);

  /* ──── Fetch ──── */
  const reqIdRef = useRef(0);
  const fetchData = useCallback(async () => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setErrorMsg(null);
    try {
      const query: SubscriptionListQuery = {
        page,
        pageSize,
        search: debouncedSearch.trim() || undefined,
        tier: tier || undefined,
        status: status || undefined,
        sortBy: "name",
        sortDir: "asc",
      };
      const res = await getSubscriptionCompanies(query);
      if (reqId !== reqIdRef.current) return;
      if (res.errors && res.errors.length > 0) {
        // CO-121 — the envelope's error shape is `{ code, description, path }`. `.message` does
        // not exist on it, so the server's reason was always dropped for the generic fallback.
        setErrorMsg(res.errors[0]?.description || t("Failed to load subscriptions"));
        setData(null);
      } else {
        setData(res.payload);
      }
    } catch {
      if (reqId !== reqIdRef.current) return;
      setErrorMsg(t("Failed to load subscriptions"));
      setData(null);
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, tier, status, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ──── Stats fetch (separate from the paged list — always reflects ALL companies). ──── */
  const fetchStats = useCallback(async () => {
    try {
      const res = await getSubscriptionStats();
      if (!res.errors || res.errors.length === 0) {
        setStatsData(res.payload);
      }
    } catch {
      // best-effort — KPI cards will fall back to page-derived numbers
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const items = useMemo(() => data?.items ?? [], [data]);
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  /* ──── Deep-link to a tab: ?tab=platform-billing ──── */
  // The repurposed enterprise-enquiry notification points here. It deliberately no longer deep-links to
  // `action=mark-paid`: on a billing-managed company that action is refused, so the old link would land
  // the Operator on a dead button instead of the tab where the live levers actually are.
  useEffect(() => {
    const requestedTab = searchParams.get("tab");
    if (requestedTab === "platform-billing" || requestedTab === "references") {
      setTab(requestedTab);
      const next = new URLSearchParams(searchParams);
      next.delete("tab");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  /* ──── Deep-link from notifications: ?companyId=N&action=mark-paid ──── */
  // When an Operator clicks a "Trial expired" notification we want to land on the
  // console with the MarkPaid dialog already open for the right company. Items load
  // async, so we wait until the page slice contains the requested company.
  useEffect(() => {
    const action = searchParams.get("action");
    const cid = searchParams.get("companyId");
    if (action !== "mark-paid" || !cid) return;
    if (items.length === 0) return;
    const idNum = Number(cid);
    const match = items.find((c) => c.id === idNum);
    if (!match) return;
    setActiveCompany(match);
    setMarkPaidOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("action");
    next.delete("companyId");
    setSearchParams(next, { replace: true });
  }, [searchParams, items, setSearchParams]);

  /* ──── KPI cards. Prefer the dedicated /stats endpoint (stable across pagination
        and filters); fall back to deriving from the visible page slice if /stats
        hasn't loaded yet (or the request failed). */
  const stats = useMemo(() => {
    if (statsData) {
      return {
        freeCount: statsData.freeCount,
        paidCount: statsData.paidCount,
        paidExpiringSoon: statsData.paidExpiringSoonCount,
      };
    }
    let freeCount = 0;
    let paidCount = 0;
    let paidExpiringSoon = 0;
    for (const c of items) {
      const ct = coerceTier(c.tier);
      if (ct === "Free") freeCount++;
      else if (ct === "Paid") {
        paidCount++;
        if (typeof c.paidDaysRemaining === "number" && c.paidDaysRemaining <= 30 && c.paidDaysRemaining > 0) {
          paidExpiringSoon++;
        }
      }
    }
    return { freeCount, paidCount, paidExpiringSoon };
  }, [items, statsData]);

  /* ──── Total in the KPI grid: prefer /stats; fall back to paged totalCount. ──── */
  const totalForKpi = statsData?.totalCompanies ?? data?.totalCount ?? 0;

  /* ──── Handlers ──── */
  const openMarkPaid = (c: CompanySubscriptionView) => {
    setActiveCompany(c);
    setMarkPaidOpen(true);
  };
  const openRevoke = (c: CompanySubscriptionView) => {
    setActiveCompany(c);
    setRevokeOpen(true);
  };
  const openHistory = (c: CompanySubscriptionView) => {
    setActiveCompany(c);
    setHistoryOpen(true);
  };

  const onActionSuccess = () => {
    toast.success(t("Subscription updated"));
    fetchData();
    fetchStats();
  };

  /* ──── Selection helpers ──── */
  const toggleSelected = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const visibleIds = useMemo(() => items.map((c) => c.id), [items]);
  const visibleSelectedCount = useMemo(
    () => visibleIds.reduce((n, id) => n + (selectedIds.has(id) ? 1 : 0), 0),
    [visibleIds, selectedIds],
  );
  const allVisibleSelected = visibleIds.length > 0 && visibleSelectedCount === visibleIds.length;
  const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected;

  const toggleAllVisible = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = visibleIds.length > 0 && visibleIds.every((id) => next.has(id));
      if (allSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [visibleIds]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const onBulkSuccess = useCallback(
    (result: BulkOperationResultView) => {
      const ok = result.succeeded.length;
      const bad = result.failed.length;
      if (bad === 0) {
        toast.success(t("{{count}} companies updated", { count: ok }));
      } else {
        // 🔴 CO-38 — A PARTIAL FAILURE OPENS THE REPORT. It used to raise a success toast with a
        // count and push the per-company rows to `console.warn`, so an operator who bulk-revoked 40
        // companies and skipped 12 had no way to learn which 12 without DevTools. The server has
        // always returned the company and the reason for every skipped row; this shows them.
        if (ok === 0) toast.error(t("All {{count}} operations failed", { count: bad }));
        else toast.success(t("{{ok}} updated, {{bad}} failed", { ok, bad }));
        setBulkFailures(result);
      }
      clearSelection();
      fetchData();
      fetchStats();
    },
    [t, clearSelection, fetchData, fetchStats],
  );

  const selectedIdsArray = useMemo(() => Array.from(selectedIds), [selectedIds]);

  const resetFilters = () => {
    setSearch("");
    setTier("");
    setStatus("");
  };

  const hasFilters = !!(search || tier || status);

  /* ──── Page chrome ──── */
  return (
    <div className="min-h-full bg-muted/20">
      <Toaster position={isRtl ? "top-left" : "top-right"} />

      {/* Header band */}
      <div className="border-b border-border bg-card">
        <div className="max-w-[1600px] mx-auto px-6 py-5 flex items-center gap-4">
          <div className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 dark:from-slate-200 dark:to-slate-400 text-white dark:text-slate-900 flex items-center justify-center shadow-sm">
            <CreditCard size={22} strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-semibold text-foreground tracking-tight">
                {t("Subscription Management")}
              </h1>
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium tabular-nums bg-muted text-muted-foreground border border-border">
                {totalCount}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t("Manage subscription tier, trial windows, and paid status across all companies.")}
            </p>
          </div>
          {/* Requests pill — opens the drawer of pending upgrade asks (Phase 7). */}
          <button
            type="button"
            onClick={() => {
              loadRequests();
              setRequestsOpen(true);
            }}
            className={cnPill(requestsCount > 0)}
            aria-label={t("Open upgrade requests")}
          >
            <Inbox size={14} aria-hidden="true" />
            <span>{t("Requests")}</span>
            <span
              className={
                requestsCount > 0
                  ? "ms-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-semibold tabular-nums"
                  : "ms-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-muted text-muted-foreground text-[10px] font-semibold tabular-nums"
              }
            >
              {requestsCount}
            </span>
          </button>
        </div>

        {/* Tab strip */}
        <div className="max-w-[1600px] mx-auto px-6">
          <div role="tablist" className="flex gap-1 -mb-px">
            <button
              role="tab"
              type="button"
              aria-selected={tab === "companies"}
              onClick={() => setTab("companies")}
              className={
                tab === "companies"
                  ? "px-3 py-2 text-sm font-medium border-b-2 border-primary text-foreground"
                  : "px-3 py-2 text-sm font-medium border-b-2 border-transparent text-muted-foreground hover:text-foreground hover:border-border transition-colors"
              }
            >
              {t("Companies")}
            </button>
            <button
              role="tab"
              type="button"
              aria-selected={tab === "platform-billing"}
              onClick={() => setTab("platform-billing")}
              className={
                tab === "platform-billing"
                  ? "px-3 py-2 text-sm font-medium border-b-2 border-primary text-foreground"
                  : "px-3 py-2 text-sm font-medium border-b-2 border-transparent text-muted-foreground hover:text-foreground hover:border-border transition-colors"
              }
            >
              {t("Platform Billing")}
            </button>
            <button
              role="tab"
              type="button"
              aria-selected={tab === "references"}
              onClick={() => setTab("references")}
              className={
                tab === "references"
                  ? "px-3 py-2 text-sm font-medium border-b-2 border-primary text-foreground"
                  : "px-3 py-2 text-sm font-medium border-b-2 border-transparent text-muted-foreground hover:text-foreground hover:border-border transition-colors"
              }
            >
              {t("References")}
            </button>
          </div>
        </div>
      </div>

      {tab === "platform-billing" && (
        <div className="max-w-[1600px] mx-auto px-6 py-6">
          <PlatformBillingPanel />
        </div>
      )}

      {tab === "references" && (
        <div className="max-w-[1600px] mx-auto px-6 py-6">
          <SignupReferencesPanel />
        </div>
      )}

      {tab === "companies" && (
      <div className="max-w-[1600px] mx-auto px-6 py-6 space-y-5">
        {/* KPI cards (dense, not hero-metric template) */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <KpiCard
            label={t("Total Companies")}
            value={totalForKpi}
            tone="slate"
            icon={<CircleDot size={14} />}
          />
          <KpiCard
            label={t("Free Tier")}
            value={stats.freeCount}
            tone="slate"
            icon={<CircleDot size={14} />}
            subline={hasFilters ? null : t("Subscriptions")}
          />
          {/*
            CO-227 — THE "TRIAL TIER" KPI CARD IS GONE. It can only ever read 0 now, and a metric
            pinned at zero forever is not information: it is a slot the operator's eye has to
            re-dismiss on every visit. The row drops 4 -> 3 columns so nothing is left hanging.
            Historical trial COUNTS remain answerable from the subscription audit log.
          */}
          <KpiCard
            label={t("Paid Tier")}
            value={stats.paidCount}
            tone="emerald"
            icon={<CheckCircle2 size={14} />}
            subline={
              stats.paidExpiringSoon > 0
                ? t("{{count}} expiring soon", { count: stats.paidExpiringSoon })
                : null
            }
          />
        </div>

        {/* Bulk actions strip — only rendered when at least one row is selected. */}
        <BulkActionsBar
          count={selectedIds.size}
          onClear={clearSelection}
          onMarkPaid={() => setBulkMarkPaidOpen(true)}
          onRevoke={() => setBulkRevokeOpen(true)}
        />

        {/* Toolbar */}
        <div className="rounded-xl border border-border bg-card">
          <div className="flex flex-wrap items-center gap-2 p-3 border-b border-border">
            {/* Search */}
            <div className="relative flex-1 min-w-[220px]">
              <Search
                size={15}
                className="absolute top-1/2 -translate-y-1/2 ltr:left-3 rtl:right-3 text-muted-foreground pointer-events-none"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("Search companies")}
                aria-label={t("Search companies")}
                className="h-9 w-full rounded-md border border-input bg-background ps-9 pe-9 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute top-1/2 -translate-y-1/2 ltr:right-2 rtl:left-2 p-1 rounded text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                  aria-label={t("Cancel")}
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Tier filter */}
            <FilterSelect
              value={tier}
              onChange={(v) => setTier(v as "" | SubscriptionTier)}
              options={TIER_OPTIONS}
              ariaLabel={t("Tier")}
            />

            {/* Status filter */}
            <FilterSelect
              value={status}
              onChange={(v) => setStatus(v as "" | SubscriptionStatus)}
              options={STATUS_OPTIONS}
              ariaLabel={t("All Statuses")}
            />

            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                <X size={14} className="me-1.5" />
                {t("Reset filters")}
              </Button>
            )}
          </div>

          {/* Table */}
          <div className="relative">
            {loading && (
              <div className="absolute inset-0 z-10 bg-card/60 backdrop-blur-[1px] flex items-center justify-center">
                <Spinner size="md" />
              </div>
            )}

            {errorMsg && !loading && (
              <div className="p-6">
                <div className="flex items-start gap-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 p-3 text-sm text-red-800 dark:text-red-300">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <span className="flex-1">{errorMsg}</span>
                  <Button variant="outline" size="sm" onClick={fetchData}>
                    {t("Retry")}
                  </Button>
                </div>
              </div>
            )}

            {!errorMsg && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="ps-4 w-10">
                      <SelectAllCheckbox
                        checked={allVisibleSelected}
                        indeterminate={someVisibleSelected}
                        disabled={visibleIds.length === 0}
                        onChange={toggleAllVisible}
                        ariaLabel={t("Select all on this page")}
                      />
                    </TableHead>
                    <TableHead>{t("Company")}</TableHead>
                    <TableHead className="text-end">{t("Users")}</TableHead>
                    <TableHead>{t("Tier")}</TableHead>
                    <TableHead>{t("Active")}</TableHead>
                    <TableHead>{t("Trial Ends")}</TableHead>
                    <TableHead>{t("Paid Until")}</TableHead>
                    <TableHead>{t("Last Updated")}</TableHead>
                    <TableHead className="text-end pe-4">
                      <span className="sr-only">{t("Actions")}</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {items.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={9} className="py-16">
                        <div className="flex flex-col items-center justify-center text-center gap-2">
                          <div className="w-12 h-12 rounded-full bg-muted/60 flex items-center justify-center text-muted-foreground">
                            <Search size={20} />
                          </div>
                          <div className="text-sm font-medium text-foreground">
                            {t("No companies found")}
                          </div>
                          <div className="text-xs text-muted-foreground max-w-xs">
                            {t("Try adjusting your filters or search term.")}
                          </div>
                          {hasFilters && (
                            <Button variant="outline" size="sm" onClick={resetFilters} className="mt-1">
                              {t("Reset filters")}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}

                  {items.map((c) => (
                    <CompanyRow
                      key={c.id}
                      company={c}
                      selected={selectedIds.has(c.id)}
                      onToggle={() => toggleSelected(c.id)}
                      onMarkPaid={() => openMarkPaid(c)}
                      onRevoke={() => openRevoke(c)}
                      onViewHistory={() => openHistory(c)}
                    />
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Pagination */}
          <div className="px-4 py-3 border-t border-border">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              pageSize={pageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onPageSizeChange={setPageSize}
              totalCount={totalCount}
            />
          </div>
        </div>
      </div>
      )}

      {/* Modals */}
      <MarkPaidDialog
        open={markPaidOpen}
        onOpenChange={setMarkPaidOpen}
        company={activeCompany}
        onSuccess={onActionSuccess}
      />
      <RevokeDialog
        open={revokeOpen}
        onOpenChange={setRevokeOpen}
        company={activeCompany}
        onSuccess={onActionSuccess}
      />
      <SubscriptionHistorySheet
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        company={activeCompany}
      />

      {/* Bulk operator dialogs */}
      <BulkMarkPaidDialog
        open={bulkMarkPaidOpen}
        onOpenChange={setBulkMarkPaidOpen}
        companyIds={selectedIdsArray}
        onSuccess={onBulkSuccess}
      />
      <BulkRevokeDialog
        open={bulkRevokeOpen}
        onOpenChange={setBulkRevokeOpen}
        companyIds={selectedIdsArray}
        onSuccess={onBulkSuccess}
      />

      <BulkFailuresDialog
        open={bulkFailures !== null}
        onOpenChange={(open) => {
          if (!open) setBulkFailures(null);
        }}
        succeededCount={bulkFailures?.succeeded.length ?? 0}
        failures={bulkFailures?.failed ?? []}
      />

      <UpgradeRequestsDrawer
        open={requestsOpen}
        onOpenChange={setRequestsOpen}
        loading={requestsLoading}
        requests={requests}
        onMarkPaid={(req) => {
          // Build a minimal CompanySubscriptionView so the existing dialog can render it.
          // Only the fields MarkPaidDialog actually reads are required.
          const synthetic: CompanySubscriptionView = {
            id: req.companyId,
            name: req.companyName,
            imageUrl: req.companyImageUrl ?? undefined,
            tier: req.currentTier,
            status: req.currentStatus,
            userCount: req.userCount,
            createdOn: req.requestedAt,
            trialDaysRemaining: req.trialDaysRemaining ?? undefined,
          };
          setActiveCompany(synthetic);
          setMarkPaidOpen(true);
          // Mark the request dismissed — it acted upon.
          dismissUpgradeRequest(req.notificationId)
            .catch(() => undefined)
            .finally(() => loadRequests());
          setRequestsOpen(false);
        }}
        onDismiss={async (req) => {
          try {
            await dismissUpgradeRequest(req.notificationId);
          } catch {
            // best-effort dismiss
          } finally {
            loadRequests();
          }
        }}
      />
    </div>
  );
}

/* ─────────────────────── Requests pill helper ─────────────────────── */
function cnPill(active: boolean) {
  const base =
    "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1";
  return active
    ? `${base} bg-amber-50 dark:bg-amber-950/40 border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40`
    : `${base} bg-muted/60 border-border text-muted-foreground hover:bg-muted`;
}

/* ─────────────────────── Upgrade Requests Drawer ─────────────────────── */
interface UpgradeRequestsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loading: boolean;
  requests: SubscriptionUpgradeRequestView[];
  onMarkPaid: (req: SubscriptionUpgradeRequestView) => void;
  onDismiss: (req: SubscriptionUpgradeRequestView) => void;
}
function UpgradeRequestsDrawer({
  open,
  onOpenChange,
  loading,
  requests,
  onMarkPaid,
  onDismiss,
}: UpgradeRequestsDrawerProps) {
  const { t } = useTranslation();

  // ESC + scroll-lock while the sheet is open. Kept lightweight (no Radix Dialog
  // here — the page already uses a mix of patterns and this sheet doesn't need
  // focus-trapping beyond the basics).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onOpenChange]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100]" aria-modal="true" role="dialog">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
        onClick={() => onOpenChange(false)}
        aria-hidden="true"
      />
      <div className="absolute end-0 top-0 h-full w-full max-w-md bg-card border-s border-border shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-border flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 flex items-center justify-center shrink-0">
            <Inbox size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-foreground">{t("Upgrade requests")}</div>
            <div className="text-xs text-muted-foreground">
              {t("Pending asks from Trial and Free customers.")}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="p-1.5 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            aria-label={t("Close")}
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading && requests.length === 0 && (
            <div className="p-6 flex items-center justify-center">
              <Spinner size="sm" />
            </div>
          )}
          {!loading && requests.length === 0 && (
            <div className="p-8 text-center text-sm text-muted-foreground">
              <Inbox size={24} className="mx-auto mb-2 opacity-50" />
              {t("No pending requests.")}
            </div>
          )}
          <ul className="divide-y divide-border">
            {requests.map((req) => {
              const tier = coerceTier(req.currentTier);
              const tv = tierVisual(tier);
              return (
                <li key={req.notificationId} className="p-4">
                  <div className="flex items-start gap-3">
                    {req.companyImageUrl ? (
                      <img
                        src={req.companyImageUrl}
                        alt=""
                        className="w-9 h-9 rounded-lg object-cover ring-1 ring-border shrink-0"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-lg bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300 flex items-center justify-center text-xs font-semibold shrink-0">
                        {initials(req.companyName)}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-foreground truncate">
                          {req.companyName}
                        </span>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${tv.pill}`}>
                          {t(tier)}
                        </span>
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                        <span className="tabular-nums">
                          {appDateTime(parseApiUtc(req.requestedAt))}
                        </span>
                        {req.requestedByName && (
                          <>
                            <span aria-hidden="true">·</span>
                            <span className="inline-flex items-center gap-1">
                              <UserIcon size={11} />
                              {req.requestedByName}
                            </span>
                          </>
                        )}
                        <span aria-hidden="true">·</span>
                        <span>{t("{{count}} users", { count: req.userCount })}</span>
                      </div>
                      {req.message && (
                        <p className="mt-2 text-xs text-foreground bg-muted/50 border border-border rounded-md p-2 whitespace-pre-wrap break-words">
                          {req.message}
                        </p>
                      )}
                      <div className="mt-3 flex items-center gap-2">
                        <Button size="sm" onClick={() => onMarkPaid(req)}>
                          <CheckCircle2 size={14} className="me-1.5" />
                          {t("Mark as Paid")}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => onDismiss(req)}>
                          {t("Dismiss")}
                        </Button>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="px-5 py-3 border-t border-border text-[11px] text-muted-foreground flex items-center gap-2">
          <Mail size={12} />
          {t("Each request also emails the Operator distribution.")}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────── KPI Card ─────────────────────── */
interface KpiProps {
  label: string;
  value: number;
  tone: "slate" | "amber" | "emerald";
  icon: React.ReactNode;
  subline?: string | null;
}
function KpiCard({ label, value, tone, icon, subline }: KpiProps) {
  const toneMap = {
    slate: {
      iconBg: "bg-slate-100 dark:bg-slate-800/80 text-slate-700 dark:text-slate-300",
      number: "text-foreground",
    },
    amber: {
      iconBg: "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300",
      number: "text-amber-700 dark:text-amber-300",
    },
    emerald: {
      iconBg: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300",
      number: "text-emerald-700 dark:text-emerald-300",
    },
  }[tone];

  return (
    <div className="rounded-xl border border-border bg-card p-4 transition-colors hover:bg-card/95">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md ${toneMap.iconBg}`}>
          {icon}
        </span>
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className={`text-2xl font-semibold tabular-nums ${toneMap.number}`}>{value}</span>
      </div>
      {subline && (
        <div className="mt-1 text-xs text-muted-foreground truncate">{subline}</div>
      )}
    </div>
  );
}

/* ───────────────── Filter Select (tiny custom for tighter chrome) ───────────────── */
interface FilterSelectProps<V extends string> {
  value: V;
  onChange: (v: V) => void;
  options: { value: V; key: string }[];
  ariaLabel: string;
}
function FilterSelect<V extends string>({ value, onChange, options, ariaLabel }: FilterSelectProps<V>) {
  const { t } = useTranslation();
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as V)}
      aria-label={ariaLabel}
      className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors"
    >
      {options.map((opt) => (
        <option key={opt.value || "all"} value={opt.value}>
          {t(opt.key)}
        </option>
      ))}
    </select>
  );
}

/* ───────────────── Select-all checkbox (indeterminate-aware) ───────────────── */
interface SelectAllCheckboxProps {
  checked: boolean;
  indeterminate: boolean;
  disabled?: boolean;
  onChange: () => void;
  ariaLabel: string;
}
function SelectAllCheckbox({
  checked,
  indeterminate,
  disabled,
  onChange,
  ariaLabel,
}: SelectAllCheckboxProps) {
  // Native checkbox doesn't expose `indeterminate` as a JSX prop — we set it
  // imperatively via a ref-callback so the visual state matches "some selected".
  const setRef = useCallback(
    (node: HTMLInputElement | null) => {
      if (node) node.indeterminate = indeterminate && !checked;
    },
    [indeterminate, checked],
  );
  return (
    <label className="inline-flex items-center cursor-pointer select-none">
      <input
        ref={setRef}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        aria-label={ariaLabel}
        className="h-4 w-4 rounded border-input text-primary focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
      />
    </label>
  );
}

/* ─────────────────────── Row ─────────────────────── */
interface RowProps {
  company: CompanySubscriptionView;
  selected: boolean;
  onToggle: () => void;
  onMarkPaid: () => void;
  onRevoke: () => void;
  onViewHistory: () => void;
}
function CompanyRow({
  company,
  selected,
  onToggle,
  onMarkPaid,
  onRevoke,
  onViewHistory,
}: RowProps) {
  const { t } = useTranslation();
  const tier = coerceTier(company.tier);
  const status = coerceStatus(company.status);
  const tv = tierVisual(tier);
  const isBadStatus = status !== "Active";

  const trial = humanizeRelative(company.trialEndsAt, t);
  const paid = tier === "Paid" && !company.paidUntil
    ? { label: t("Indefinite"), tone: "ok" as const, delta: null as number | null }
    : humanizeRelative(company.paidUntil, t, company.paidUntil ? "—" : (tier === "Paid" ? t("Indefinite") : "—"));

  // Action gating:
  //   - Mark as Paid: always
  //   - Revoke: only if tier !== Free
  //
  // CO-227 — "Extend Trial" and "Start Trial" are gone from this menu. They were the operator's two
  // trial-grant levers; their endpoints no longer exist, so leaving the items would have produced a
  // 404 toast on click. Mark as Paid is the surviving way to grant a tenant time.
  const canRevoke = tier !== "Free";

  return (
    <TableRow data-state={selected ? "selected" : undefined}>
      {/* Select */}
      <TableCell className="ps-4 w-10">
        <label className="inline-flex items-center cursor-pointer select-none">
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggle}
            className="h-4 w-4 rounded border-input text-primary focus:ring-2 focus:ring-primary/20"
            aria-label={t("Select {{name}}", { name: company.name })}
          />
        </label>
      </TableCell>

      {/* Company */}
      <TableCell>
        <div className="flex items-center gap-3 min-w-0">
          {company.imageUrl ? (
            <img
              src={company.imageUrl}
              alt=""
              className="w-9 h-9 rounded-lg object-cover ring-1 ring-border shrink-0"
            />
          ) : (
            <div className="w-9 h-9 rounded-lg bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300 flex items-center justify-center text-xs font-semibold shrink-0">
              {initials(company.name)}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-sm font-medium text-foreground truncate">{company.name}</div>
            {company.email && (
              <div className="text-xs text-muted-foreground truncate">{company.email}</div>
            )}
          </div>
        </div>
      </TableCell>

      {/* Users */}
      <TableCell className="text-end tabular-nums text-sm text-foreground">
        {company.userCount}
      </TableCell>

      {/* Tier */}
      <TableCell>
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tv.pill}`}
        >
          {t(tier)}
        </span>
      </TableCell>

      {/* Status */}
      <TableCell>
        <span
          className={`inline-flex items-center gap-1.5 text-xs ${
            isBadStatus
              ? "text-red-700 dark:text-red-300"
              : "text-emerald-700 dark:text-emerald-400"
          }`}
        >
          {isBadStatus ? (
            <CircleAlert size={14} aria-hidden="true" />
          ) : (
            <CheckCircle2 size={14} aria-hidden="true" />
          )}
          {t(status === "TrialExpired" ? "Trial Expired" : status === "PaymentOverdue" ? "Payment Overdue" : "Active")}
        </span>
      </TableCell>

      {/* Trial Ends */}
      <TableCell>
        <DateCell tone={trial.tone} label={trial.label} bold={trial.tone === "soon" || trial.tone === "expired"} />
      </TableCell>

      {/* Paid Until */}
      <TableCell>
        <DateCell tone={paid.tone} label={paid.label} bold={paid.tone === "soon" || paid.tone === "expired"} />
      </TableCell>

      {/* Last Updated */}
      <TableCell>
        <span
          className="text-xs text-muted-foreground tabular-nums"
          title={company.subscriptionUpdatedAt ? appDateTime(parseApiUtc(company.subscriptionUpdatedAt)) : ""}
        >
          {relativeTime(company.subscriptionUpdatedAt, t)}
        </span>
      </TableCell>

      {/* Actions */}
      <TableCell className="text-end pe-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center justify-center w-8 h-8 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t("Actions")}
            >
              <MoreHorizontal size={16} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[200px]">
            <DropdownMenuItem onClick={onMarkPaid}>
              <CheckCircle2 size={14} className="me-2 text-emerald-600 dark:text-emerald-400" />
              {t("Mark as Paid")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onViewHistory}>
              <HistoryIcon size={14} className="me-2 text-muted-foreground" />
              {t("View History")}
            </DropdownMenuItem>
            {canRevoke && (
              <DropdownMenuItem
                onClick={onRevoke}
                className="text-red-700 dark:text-red-300 focus:text-red-800 dark:focus:text-red-200"
              >
                <Ban size={14} className="me-2" />
                {t("Revoke Subscription")}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}

/* ─────────────────────── Date Cell ─────────────────────── */
function DateCell({ tone, label, bold }: { tone: "ok" | "soon" | "expired" | "muted"; label: string; bold?: boolean }) {
  const toneClass =
    tone === "expired"
      ? "text-red-700 dark:text-red-300"
      : tone === "soon"
        ? "text-amber-700 dark:text-amber-300"
        : tone === "muted"
          ? "text-muted-foreground"
          : "text-foreground";
  return (
    <span className={`text-xs tabular-nums ${toneClass} ${bold ? "font-semibold" : ""}`}>
      {label}
    </span>
  );
}
